/**
 * Broker Settings — phase 4 of broker integration.
 *
 * Lists every broker in the registry alongside its live link status, lets
 * users start/finish an OAuth link, refresh access tokens, or unlink an
 * account entirely. In production this panel also surfaces per-broker
 * account sub-lists (cash / margin / corporate) once linked.
 *
 * Every action here flows through the BAL: link-begin -> broker consent
 * -> link-complete -> Secrets Manager. The UI only shows state; it never
 * holds credentials.
 */
import { useEffect, useState } from 'react';
import { linkBroker, unlinkBroker, refreshToken, rowsForSettings, type BrokerLinkRow } from '../lib/broker-link';

export function BrokerSettings() {
  const [rows, setRows] = useState<BrokerLinkRow[]>([]);
  const [busyId, setBusyId] = useState<string | undefined>();
  const [steps, setSteps] = useState<string[]>([]);
  const [openId, setOpenId] = useState<string | undefined>();
  const [toast, setToast] = useState<string | undefined>();

  const reload = () => setRows(rowsForSettings());
  useEffect(() => { reload(); }, []);
  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(undefined), 3000); return () => clearTimeout(t); } }, [toast]);

  const doLink = async (id: string) => {
    setBusyId(id); setSteps([]); setOpenId(id);
    try {
      await linkBroker(id as any, { onProgress: (s) => setSteps((p) => [...p, s]) });
      reload();
      setToast(`Linked ${id} successfully.`);
    } catch (e: any) {
      setToast('Link failed: ' + (e?.message ?? 'unknown'));
    } finally {
      setBusyId(undefined);
    }
  };
  const doUnlink = async (id: string) => {
    if (!confirm(`Unlink ${id}? Existing positions remain; you will not be able to place new orders until you re-link.`)) return;
    setBusyId(id);
    try { await unlinkBroker(id as any); reload(); setToast(`Unlinked ${id}.`); }
    finally { setBusyId(undefined); }
  };
  const doRefresh = async (id: string) => {
    setBusyId(id);
    try { await refreshToken(id as any); reload(); setToast(`Refreshed ${id} access token.`); }
    finally { setBusyId(undefined); }
  };

  return (
    <div className="bs">
      <div className="bs-head">
        <h2>Broker Integrations</h2>
        <div className="bs-sub">Link SEC-licensed brokers to trade through NigaStock. Credentials are held in AWS Secrets Manager scoped to your Cognito identity &mdash; they never touch your browser.</div>
      </div>

      <div className="bs-grid">
        {rows.map(({ spec, link }) => (
          <div key={spec.id} className={'bs-card ' + ('bs-status-' + link.status)}>
            <div className="bs-card-h">
              <div className="bs-logo">{spec.logoGlyph}</div>
              <div>
                <div className="bs-name">{spec.label}</div>
                <div className="bs-tag">
                  Commission {spec.commissionBps}bps &middot; Reliability {(spec.reliabilityScore * 100).toFixed(1)}%
                </div>
              </div>
              <div className={'bs-status-badge ' + link.status}>{link.status.toUpperCase()}</div>
            </div>
            <div className="bs-caps">
              {spec.supports.EQUITY && <span className="bs-cap">EQUITY</span>}
              {spec.supports.ETF && <span className="bs-cap">ETF</span>}
              {spec.supports.BOND && <span className="bs-cap">BOND</span>}
              {spec.supports.FX && <span className="bs-cap">FX</span>}
            </div>
            {link.status === 'linked' && (
              <div className="bs-meta">
                <div><span className="k">Accounts</span><span className="v">{link.linkedAccounts ?? 1}</span></div>
                <div><span className="k">Linked</span><span className="v">{link.linkedAt?.slice(0, 10) ?? '\u2014'}</span></div>
                <div><span className="k">Last refresh</span><span className="v">{link.lastRefreshAt ? new Date(link.lastRefreshAt).toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' }) : '\u2014'}</span></div>
              </div>
            )}
            {link.errorMessage && <div className="bs-error">{link.errorMessage}</div>}
            <div className="bs-actions">
              {link.status === 'linked' ? (
                <>
                  <button className="bs-btn" disabled={busyId === spec.id} onClick={() => doRefresh(spec.id)}>{busyId === spec.id ? 'Refreshing\u2026' : 'Refresh Token'}</button>
                  <button className="bs-btn bs-danger" disabled={busyId === spec.id} onClick={() => doUnlink(spec.id)}>Unlink</button>
                </>
              ) : link.status === 'pending' ? (
                <button className="bs-btn" disabled>Linking\u2026</button>
              ) : (
                <button className="bs-btn bs-primary" disabled={busyId === spec.id} onClick={() => doLink(spec.id)}>
                  {busyId === spec.id ? 'Linking\u2026' : 'Link Account'}
                </button>
              )}
              <button className="bs-btn bs-ghost" onClick={() => setOpenId(openId === spec.id ? undefined : spec.id)}>
                {openId === spec.id ? 'Hide details' : 'Details'}
              </button>
            </div>
            {openId === spec.id && (
              <div className="bs-detail">
                <div className="bs-detail-h">Link progress</div>
                <div className="bs-steps">
                  {steps.length === 0
                    ? <div className="bs-step muted">No active link. Click "Link Account" to start the OAuth 2.0 PKCE flow.</div>
                    : steps.map((s, i) => <div key={i} className="bs-step">&#x2713; {s}</div>)
                  }
                </div>
                <div className="bs-security">
                  <strong>What happens on link:</strong>
                  <ol>
                    <li>Browser redirects to {spec.label}&apos;s OAuth server.</li>
                    <li>You authenticate with your existing {spec.label} account + MFA.</li>
                    <li>{spec.label} returns an authorization code to NigaStock.</li>
                    <li>BAL exchanges the code for a refresh_token and stores it in AWS Secrets Manager under your Cognito sub.</li>
                    <li>Access tokens never leave AWS; browser gets only a link status.</li>
                  </ol>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {toast && <div className="bs-toast">{toast}</div>}
    </div>
  );
}
